import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import loaderGif from './process-mining-loader-720-v2.gif';

// Total time for the entire loading process in milliseconds
const TOTAL_LOADING_TIME = 40000;

// Loading steps
const LOADING_STEPS = ['Analyzing', 'Mapping', 'Visualizing'] as const;
const LOADING_DOTS = ['...', '.', '..'] as const;

// Time per step in milliseconds
const TIME_PER_STEP = TOTAL_LOADING_TIME / (LOADING_STEPS.length - 1);

// Dots animation timing
const DOTS_NORMAL_INTERVAL = 500;
const DOTS_PAUSE_DURATION = 3000;

interface MetadataMappingLoaderProps {
  overlayOpacity?: number;
}

export const MetadataMappingLoader = ({ overlayOpacity = 1 }: MetadataMappingLoaderProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentDotsStep, setCurrentDotsStep] = useState<number>(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let nextStep = currentStep + 1;
      if (nextStep >= LOADING_STEPS.length) {
        nextStep = 0;
      }
      setCurrentStep(nextStep);
    }, TIME_PER_STEP);

    return () => {
      clearTimeout(timeout);
      setCurrentDotsStep(0);
    };
  }, [currentStep]);

  useEffect(() => {
    const delay = currentDotsStep === 0 ? DOTS_PAUSE_DURATION : DOTS_NORMAL_INTERVAL;
    const timeout = setTimeout(() => {
      let nextDotsStep = currentDotsStep + 1;
      if (nextDotsStep >= LOADING_DOTS.length) {
        nextDotsStep = 0;
      }
      setCurrentDotsStep(nextDotsStep);
    }, delay);
    return () => clearTimeout(timeout);
  }, [currentDotsStep]);

  const displayText = LOADING_STEPS[currentStep];
  const dots = LOADING_DOTS[currentDotsStep];
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `rgba(255, 255, 255, ${overlayOpacity})`,
        zIndex: 1001,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <img src={loaderGif} alt="Process Mining Loader" width={264} height={264} />
        <Box
          sx={{
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '19px' }}>
              <Typography variant="h4" color={colors.black}>
                {displayText}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '19px',
                    textAlign: 'left',
                  }}
                >
                  {dots}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
